<script setup lang="ts">
import { computed, ComputedRef, onMounted, Ref, ref, watch } from 'vue'
import { useMeasurementStore } from '@/store/measurements'
import { BaseSerializedChart, BaseSerializedInfluxLineChart } from '@/composables/charts/influx-charts/chart-builders/ChartInterfaces'
import { CHART_TYPES } from '@/composables/charts/influx-charts/chart-builders/CHART_TYPES'
import { SerializedGeneralChartTimeSetup } from '@/composables/charts/influx-charts/chart-serialization/ChartTimeSetup'
import XAggregationPicker from '@/components/charts/chart-creator/XAggregationPicker.vue'
import { TagSelector, type xAggregations } from '@/composables/charts/influx-charts/query-builders/InfluxTypes'
import GroupByPicker from '@/components/charts/chart-creator/GroupByPicker.vue'
import FillPicker from '@/components/charts/chart-creator/FillPicker.vue'
import MultiTagSelector from '@/components/charts/chart-creator/MultiTagSelector.vue'
import _ from 'lodash'
import { useDisplay } from 'vuetify'

const props = withDefaults(
  defineProps<{ baseSerialized: BaseSerializedChart | null; timeSetup: SerializedGeneralChartTimeSetup | null; label?: string }>(),
  {
    label: '',
  }
)
const model = defineModel<BaseSerializedInfluxLineChart | null>()

const { mobile } = useDisplay()

const availableMeasurements: Ref<string[]> = ref([])
const availableFields: Ref<string[]> = ref([])
const availableTagKeys: Ref<string[]> = ref([])
const pickedMeasurement: Ref<string | null> = ref(null)
const pickedField: Ref<string | null> = ref(null)
const xAggregation: Ref<xAggregations> = ref('mean')
const groupByTags: Ref<string[]> = ref([])
const fill: Ref<number | 'linear' | 'none' | 'previous' | null> = ref(null)
const tagSelectors: Ref<TagSelector<string>[]> = ref([])

const compiledModel: ComputedRef<BaseSerializedInfluxLineChart | null> = computed(() => {
  if (!props.baseSerialized) return null
  if (!props.timeSetup) return null
  if (!pickedMeasurement.value) return null
  if (!pickedField.value) return null
  return {
    ...props.baseSerialized,
    groupByTags: groupByTags.value,
    xAggregation: xAggregation.value,
    fill: fill.value,
    timeSetup: props.timeSetup,
    measurement: pickedMeasurement.value,
    field: pickedField.value,
    chartType: CHART_TYPES.LINE,
    tagSelectors: tagSelectors.value,
  }
})

async function loadFields() {
  availableFields.value = await useMeasurementStore().loadAvailableFieldsForMeasurement(undefined, pickedMeasurement.value)
  if (!pickedField.value || !availableFields.value.includes(pickedField.value)) pickedField.value = availableFields.value[0]
}
async function loadTagKeys() {
  availableTagKeys.value = await useMeasurementStore().loadAvailableTagKeyForMeasurement(undefined, pickedMeasurement.value)
}

async function loadMeasurements() {
  availableMeasurements.value = await useMeasurementStore().loadAvailableMeasurements()
  if (!pickedMeasurement.value || !availableMeasurements.value.includes(pickedMeasurement.value))
    pickedMeasurement.value = availableMeasurements.value[0]
}

onMounted(async () => {
  await loadMeasurements()
})

watch(pickedMeasurement, async () => {
  await loadFields()
  await loadTagKeys()
})

watch(
  () => [props.baseSerialized, props.timeSetup, xAggregation, groupByTags, fill, pickedMeasurement, pickedField, tagSelectors],
  () => {
    if (!compiledModel.value) return
    if (_.isEqual(model.value, compiledModel.value)) return

    model.value = compiledModel.value
  },
  { immediate: true, deep: true }
)

watch(
  model,
  () => {
    if (!model.value) return
    if (_.isEqual(model.value, compiledModel.value)) return

    pickedMeasurement.value = model.value.measurement
    pickedField.value = model.value.field
    xAggregation.value = model.value.xAggregation
    groupByTags.value = model.value.groupByTags || []
    fill.value = model.value.fill || null
    tagSelectors.value = model.value.tagSelectors || []
  },
  { deep: true, immediate: true }
)
</script>

<template>
  <v-layout-card
    class="pa-0"
    variant="flat"
    elevation="0"
  >
    <v-card-title class="pt-0">
      <v-icon
        icon="mdi-tune"
        start
      />
      {{ props.label || $t('chart_sharing.creator.customize.line_settings.title') }}
    </v-card-title>
    <v-card-text
      class="py-2"
      :class="{ 'px-0': mobile, 'px-6': !mobile }"
    >
      <v-autocomplete
        v-model="pickedMeasurement"
        prepend-icon="mdi-ruler-square"
        variant="outlined"
        :label="$t('chart_sharing.creator.customize.chart_details.measurement')"
        :items="availableMeasurements"
        :item-title="item => $t(`chart_sharing.creator.customize.line_settings.measurement.${item}`)"
        :item-value="item => item"
      />

      <v-autocomplete
        v-model="pickedField"
        prepend-icon="mdi-grid"
        variant="outlined"
        :label="$t('chart_sharing.creator.customize.chart_details.field')"
        :items="availableFields"
        :item-title="item => $t(`chart_sharing.creator.customize.line_settings.field.${item}`)"
        :item-value="item => item"
      />

      <XAggregationPicker v-model="xAggregation" />

      <GroupByPicker
        v-model="groupByTags"
        :measurement="pickedMeasurement"
      />

      <FillPicker v-model="fill" />
    </v-card-text>

    <v-divider
      class="my-8"
      thickness="2"
    />

    <MultiTagSelector
      v-if="pickedMeasurement"
      v-model="tagSelectors"
      :measurement="pickedMeasurement"
    />
  </v-layout-card>
</template>
